import React, { useCallback, useEffect, useState } from 'react';
import { Dialog } from '@progress/kendo-react-dialogs';
import { Input } from '@progress/kendo-react-inputs';
import { Button } from '@progress/kendo-react-buttons';
import { toDataSourceRequestString } from '@progress/kendo-data-query';
import { fetchApi } from '../../../services/api';
import { JsonResponse } from '../../../TypeGen/json-response';

interface LocationViewModel {
  LocationID: number;
  Name: string;
  IsActive: boolean;
  PartCount: number;
  TotalQuantity: number;
}

interface LocationsResponse {
  Items: {
    Data: LocationViewModel[];
    Total: number;
  };
}

interface ManageLocationsDialogProps {
  onClose: () => void;
  onChanged: () => void;
}

const ManageLocationsDialog: React.FC<ManageLocationsDialogProps> = ({ onClose, onChanged }) => {
  const [loading, setLoading] = useState(false);
  const [locations, setLocations] = useState<LocationViewModel[]>([]);
  const [names, setNames] = useState<Record<number, string>>({});
  const [busyId, setBusyId] = useState<number | null>(null);

  const loadLocations = useCallback(async () => {
    setLoading(true);
    try {
      const request = toDataSourceRequestString({ skip: 0, take: 200, sort: [{ field: 'Name', dir: 'asc' }] });
      const response: LocationsResponse = await fetchApi(`/api/ShopInventory/Locations?${request}`);
      setLocations(response.Items.Data);
      setNames(Object.fromEntries(response.Items.Data.map((l) => [l.LocationID, l.Name])));
    } catch (error) {
      console.error('Error loading locations:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadLocations();
  }, [loadLocations]);

  const post = async (endpoint: string, payload: object): Promise<boolean> => {
    try {
      const response: JsonResponse = await fetchApi(endpoint, payload, 'POST');
      if (response.Success === false) {
        alert(response.ErrorMessage || 'Operation failed.');
        return false;
      }
      return true;
    } catch (e: any) {
      if (!e?.status) alert('Operation failed.');
      return false;
    }
  };

  const handleRename = async (location: LocationViewModel) => {
    const name = (names[location.LocationID] ?? '').trim();
    if (!name) {
      alert('Location name is required.');
      return;
    }
    setBusyId(location.LocationID);
    const ok = await post('/api/ShopInventory/UpdateLocation', { LocationID: location.LocationID, Name: name, IsActive: location.IsActive });
    setBusyId(null);
    if (ok) {
      await loadLocations();
      onChanged();
    }
  };

  const handleToggleActive = async (location: LocationViewModel) => {
    setBusyId(location.LocationID);
    const ok = await post('/api/ShopInventory/UpdateLocation', { LocationID: location.LocationID, Name: location.Name, IsActive: !location.IsActive });
    setBusyId(null);
    if (ok) {
      await loadLocations();
      onChanged();
    }
  };

  const handleDelete = async (location: LocationViewModel) => {
    if (!window.confirm(`Delete location "${location.Name}"? This cannot be undone.`)) {
      return;
    }
    setBusyId(location.LocationID);
    const ok = await post('/api/ShopInventory/DeleteLocation', { LocationID: location.LocationID });
    setBusyId(null);
    if (ok) {
      await loadLocations();
      onChanged();
    }
  };

  return (
    <Dialog title="Manage Locations" onClose={onClose} width={640}>
      {loading ? (
        <p className="text-muted">Loading...</p>
      ) : locations.length === 0 ? (
        <p className="text-muted">No locations configured.</p>
      ) : (
        <div className="table-responsive">
          <table className="table table-sm align-middle">
            <thead>
              <tr>
                <th>Name</th>
                <th>Parts</th>
                <th>Qty</th>
                <th>Status</th>
                <th className="text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {locations.map((location) => {
                const isBusy = busyId === location.LocationID;
                const renamed = (names[location.LocationID] ?? '') !== location.Name;
                return (
                  <tr key={location.LocationID}>
                    <td style={{ minWidth: 220 }}>
                      <Input
                        value={names[location.LocationID] ?? ''}
                        disabled={isBusy}
                        maxLength={128}
                        onChange={(e) => setNames((prev) => ({ ...prev, [location.LocationID]: e.value ?? '' }))}
                      />
                    </td>
                    <td>{location.PartCount}</td>
                    <td>{location.TotalQuantity}</td>
                    <td>
                      {location.IsActive
                        ? <span className="badge badge-success">Active</span>
                        : <span className="badge badge-secondary">Inactive</span>}
                    </td>
                    <td className="text-right text-nowrap">
                      <Button
                        size="small"
                        themeColor="primary"
                        disabled={isBusy || !renamed}
                        onClick={() => handleRename(location)}
                      >
                        Save
                      </Button>
                      <Button
                        size="small"
                        className="ml-1"
                        disabled={isBusy}
                        onClick={() => handleToggleActive(location)}
                      >
                        {location.IsActive ? 'Deactivate' : 'Activate'}
                      </Button>
                      <Button
                        size="small"
                        themeColor="error"
                        className="ml-1"
                        disabled={isBusy}
                        onClick={() => handleDelete(location)}
                      >
                        Delete
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Dialog>
  );
};

export default ManageLocationsDialog;
